"use strict";
/*
  Vereign Seal integration library. Copyright (C) 2020-2021 Vereign AG

  This program is free software: you can redistribute it and/or modify it under the terms of the GNU Affero General
  Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option)
  any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied
  warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
  details.

  You should have received a copy of the GNU Affero General Public License along with this program. If not, see
  https://www.gnu.org/licenses/.

  In case of queries or questions, please contact us under by email under contact@vereign.com or by letter post to
  Vereign AG, Kolinplatz 10, 6300 Zug, Switzerland
*/
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.findSealContainer = void 0;
const lib_mime_1 = __importDefault(require("@vereign/lib-mime"));
const lib_png_1 = __importStar(require("@vereign/lib-png"));
const url_parse_1 = __importDefault(require("url-parse"));
const SealReadingError_1 = __importDefault(require("./SealReadingError"));
const sealTemplate_1 = require("./sealTemplate");
const getSealId_1 = __importDefault(require("./getSealId"));
/**
 * An array of possible URL wrappings created by email Vendors
 */
const OUTLOOK_SAFELINKS_WRAPPER = "safelinks.protection.outlook.com";
exports.default = (mime, parseHTML = null) => __awaiter(void 0, void 0, void 0, function* () {
    if (typeof mime === "string") {
        mime = new lib_mime_1.default(mime);
    }
    if (parseHTML) {
        mime.parseHTML = parseHTML;
    }
    const { htmlDocument } = yield mime.extractQuotedPartFromHTML();
    const sealContainer = exports.findSealContainer(htmlDocument.body);
    if (!sealContainer) {
        throw new SealReadingError_1.default("Message not sealed", SealReadingError_1.default.MESSAGE_NOT_SEALED, SealReadingError_1.default.SEVERITY_INFO);
    }
    const sealUrl = extractSealUrl(sealContainer);
    const sealId = yield getSealId_1.default(sealUrl);
    const publicKey = extractSealPublicKey(sealContainer, mime);
    return { sealUrl, sealId, publicKey };
});
/**
 * BFS for HTML tree
 * @param root
 */
const findSealContainer = (root) => {
    const queue = [];
    queue.unshift(root);
    while (queue.length) {
        const element = queue.shift();
        const id = element.getAttribute("id");
        if (element.nodeName === "A" && (id === null || id === void 0 ? void 0 : id.includes(sealTemplate_1.SEAL_CONTAINER_PREFIX))) {
            return element;
        }
        for (const childNode of element.childNodes) {
            childNode.nodeType === 1 && queue.push(childNode);
        }
    }
};
exports.findSealContainer = findSealContainer;
const extractSealPublicKey = (sealContainer, mime) => {
    const sealImages = sealContainer.getElementsByTagName("img");
    if (sealImages.length === 0) {
        throw new SealReadingError_1.default("Seal not found", SealReadingError_1.default.SEAL_NOT_FOUND, SealReadingError_1.default.SEVERITY_WARNING);
    }
    else if (sealImages.length > 1) {
        throw new SealReadingError_1.default("Multiple seal images found", SealReadingError_1.default.SEAL_NOT_TRUSTWORTHY);
    }
    const sealImage = sealImages[0];
    const attachmentsParts = mime.getAttachments();
    const sealImageAttachmentPart = attachmentsParts.find((part) => {
        var _a;
        const contentId = ((_a = part.headers["content-id"]) === null || _a === void 0 ? void 0 : _a.length) ? part.headers["content-id"][0].replace(/[<>]/g, "")
            : "";
        return `cid:${contentId}` === sealImage.getAttribute("src");
    });
    const sealImageAttachment = sealImageAttachmentPart && mime.getAttachment(sealImageAttachmentPart);
    if (!sealImageAttachment) {
        throw new SealReadingError_1.default("Seal not found", SealReadingError_1.default.SEAL_NOT_FOUND, SealReadingError_1.default.SEVERITY_WARNING);
    }
    const png = lib_png_1.default.readPng(Buffer.from(sealImageAttachment.base64, "base64"));
    const metadata = lib_png_1.getMetaData(png);
    const sealMarker = metadata["v-seal"];
    if (!sealMarker) {
        throw new SealReadingError_1.default('Seal not found. Seal marker "v-seal" is missing in picture metadata', SealReadingError_1.default.SEAL_NOT_FOUND, SealReadingError_1.default.SEVERITY_WARNING);
    }
    const { publicKey } = metadata;
    return publicKey;
};
const extractSealUrl = (sealContainer) => {
    const sealUrl = sealContainer === null || sealContainer === void 0 ? void 0 : sealContainer.getAttribute("href");
    let parsedSealUrl = new url_parse_1.default(sealUrl, true);
    if (parsedSealUrl.host.includes(OUTLOOK_SAFELINKS_WRAPPER)) {
        parsedSealUrl = new url_parse_1.default(parsedSealUrl.query.url, true);
    }
    if (!(parsedSealUrl === null || parsedSealUrl === void 0 ? void 0 : parsedSealUrl.href)) {
        throw new SealReadingError_1.default("Seal URL not found", SealReadingError_1.default.SEAL_NOT_FOUND, SealReadingError_1.default.SEVERITY_WARNING);
    }
    return parsedSealUrl.href;
};
